import { LegalLayout } from '../components/LegalLayout';

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="July 5, 2026">
      <p>
        This Privacy Policy describes how <strong>PROTBYTE</strong> (
        <strong>"we"</strong>) collects, uses, and shares information when you use
        PROTBYTE Sentinel (the <strong>"Service"</strong>). The Service is designed
        to collect as little information as possible.{' '}
        <strong>Scan results are not persisted on our servers.</strong>
      </p>

      <h2>1. Information we collect</h2>

      <h3>Information you submit</h3>
      <ul>
        <li>
          <strong>The domain name you scan.</strong> The domain is processed in
          memory to produce the report. The full scan result — DNS records,
          certificates, exposure findings, and so on — is returned to your browser
          and discarded server-side. We do <em>not</em> store scan results on our
          servers. Your browser keeps the result in <code>sessionStorage</code> for
          the current tab (so a refresh or the back button preserves it, and you can
          export it to PDF); it never leaves your browser and is cleared when you
          close the tab.
        </li>
        <li>
          <strong>Aggregate operator log.</strong> For service-quality and abuse
          monitoring, we keep a small, aggregate log row per scan. It contains the
          domain name, a timestamp, the overall and per-module security scores, the
          email-authentication grade, counts of findings by severity, exposure and
          vulnerability counts, the scan duration, a success/error status (with a
          coarse error category), which analyzer modules returned no data, and the
          Cloudflare-attributed country (for example, "US"). We do <em>not</em> log
          IP addresses, user agents, full scan results, or any data that ties an
          individual to a scan. This log is visible only to the operator behind a
          zero-trust login (Cloudflare Access) and is never shared or sold.
        </li>
      </ul>

      <h3>Information collected automatically</h3>
      <ul>
        <li>
          <strong>IP address.</strong> Cloudflare attaches your IP address
          (<code>cf-connecting-ip</code>) to your request so we can apply per-IP
          rate limits. Rate-limit counters live in Cloudflare KV and auto-expire
          shortly after the rate-limit window closes (a minute or two at most). We
          do not write your IP address to our operator log.
        </li>
        <li>
          <strong>Human-verification (Cloudflare Turnstile).</strong> To prevent
          automated abuse of the scanner, the scan form uses Cloudflare Turnstile.
          Turnstile assesses your browser and connection to distinguish humans from
          bots, and may read and set cookies or local storage and process technical
          signals for that purpose. Turnstile is designed to be privacy-preserving
          and is not used for cross-site advertising or behavioral tracking. This
          processing is governed by{' '}
          <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer">Cloudflare's privacy policy</a>.
        </li>
        <li>
          <strong>Cached third-party API responses.</strong> To keep scans fast,
          public RDAP, Certificate Transparency, and CVE-database lookups are cached
          in Cloudflare KV for up to 24 hours. DNS responses are cached at the
          Cloudflare edge cache rather than KV. In all cases the cache key is the
          public lookup parameter (domain name and record type) — no IP address or
          per-user identifier.
        </li>
        <li>
          <strong>Standard request metadata</strong> (user-agent, time, URL path) is
          processed by Cloudflare to deliver the Service and protect it from abuse,
          in line with Cloudflare's policies.
        </li>
      </ul>

      <h3>Information we do NOT collect</h3>
      <ul>
        <li>We do not require or offer end-user accounts.</li>
        <li>We do not store scan results, history, or any record of past scans on our servers.</li>
        <li>We do not store email addresses, run watchlists, or send alerts.</li>
        <li>We do not set advertising cookies or third-party tracking pixels.</li>
        <li>We do not sell or rent any data.</li>
      </ul>
      <p>
        Note: Cloudflare may set strictly-necessary security cookies (for example,
        for bot management or the Turnstile challenge) to deliver and protect the
        Service. These are functional, not advertising, cookies.
      </p>

      <h2>2. How we use information</h2>
      <ul>
        <li>To produce the scan report you requested and return it to your browser.</li>
        <li>To verify that requests come from a human and not an automated client.</li>
        <li>To enforce per-IP rate limits and prevent abuse.</li>
        <li>To monitor service quality and diagnose and fix bugs in the Service.</li>
      </ul>

      <h2>3. How information is shared</h2>
      <p>We do not sell or rent any information. We share only as described:</p>
      <ul>
        <li>
          <strong>Cloudflare</strong> hosts the Service (Workers, Pages, KV, D1,
          Access) and provides DNS-over-HTTPS lookups and the Turnstile
          human-verification challenge. Its data handling is governed by{' '}
          <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer">Cloudflare's privacy policy</a>.
        </li>
        <li>
          <strong>Public data sources</strong> queried during a scan see the domain
          name (and our user-agent), but not your IP, because lookups are made
          server-to-server from Cloudflare Workers. These include the IANA RDAP
          bootstrap registry and TLD-operated RDAP servers, certspotter and crt.sh
          (Certificate Transparency mirrors), Team Cymru (IP-to-ASN lookup, over
          DNS), and OSV.dev (CVE database).
        </li>
        <li>
          We may disclose information if required by law, regulation, or legal
          process, or to protect the rights, property, or safety of PROTBYTE,
          our users, or the public.
        </li>
      </ul>

      <h2>4. Data retention</h2>
      <ul>
        <li>Scan <em>results</em>: <strong>not retained on our servers</strong>. The result is held only in your browser's <code>sessionStorage</code> for the current tab — a refresh or the back button keeps it; closing the tab discards it.</li>
        <li>Aggregate operator log (domain, timestamp, scores, severity counts, duration, status, country — no IPs or PII): retained for service-quality and abuse monitoring. Email <a href="mailto:contact@protbyte.com">contact@protbyte.com</a> with a domain to remove specific entries.</li>
        <li>Rate-limit counters: expire automatically, typically within one to two minutes of the request.</li>
        <li>Cached third-party responses (RDAP / CT logs / OSV): expire from KV within 24 hours. DNS responses cached at the Cloudflare edge similarly expire within 24 hours.</li>
      </ul>

      <h2>5. Your rights</h2>
      <p>
        Because we do not retain personal data tied to you, there is generally
        nothing for us to access, correct, or delete on request. Depending on where
        you live, you may have rights under laws such as the GDPR or the CCPA/CPRA;
        we will honor applicable requests to the extent any relevant data exists. If
        you have a question or concern about your data, email us at{' '}
        <a href="mailto:contact@protbyte.com">contact@protbyte.com</a>.
      </p>

      <h2>6. Security</h2>
      <p>
        Data is encrypted in transit (TLS) within Cloudflare's infrastructure. The
        operator dashboard is protected by Cloudflare Access (zero-trust login). We
        follow industry best practices but cannot guarantee absolute security.
      </p>

      <h2>7. Children</h2>
      <p>
        The Service is not directed at children under 13, and we do not knowingly
        collect personal information from them.
      </p>

      <h2>8. International transfers</h2>
      <p>
        The Service is delivered from Cloudflare's globally distributed edge network.
        Requests are processed in any region where Cloudflare operates.
      </p>

      <h2>9. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. The <em>"Last updated"</em> date
        at the top reflects the most recent revision.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions or requests regarding this Privacy Policy can be sent to{' '}
        <a href="mailto:contact@protbyte.com">contact@protbyte.com</a>.
      </p>
    </LegalLayout>
  );
}
